/** Formatters ported from the mockups. Pure but for one input: the page locale.
 *
 * English output is exactly what it was before translations existed. A
 * translated page gets its own digit grouping and decimal mark, and money stays
 * USD but is labelled the local way, because a bare `$` reads as the local
 * currency in Brazil and much of Latin America. What Intl writes for $0.40:
 * `US$ 0,40` in pt-BR, `US$0.40` in zh-Hans and es, `0,40 $US` in fr, and the
 * ISO code in de, ru and it (`0,40 USD`; de and ru would otherwise print a
 * bare `0,40 $`, and it already prints the code). */
import { getLocale, type Locale } from '../paraglide/runtime';
import { m } from '../paraglide/messages';

const isEnglish = (): boolean => getLocale() === 'en';

/**
 * The Intl locale for figures, where it is not the page locale. Spanish uses
 * Cuban Spanish because its CLDR data has es-419's digits, dates and month names
 * and also writes USD as `US$0.40`; es-419 itself writes `USD 0.40`.
 */
const INTL_LOCALE: Partial<Record<Locale, string>> = { en: 'en-US', es: 'es-CU' };

const intlLocale = (): string => INTL_LOCALE[getLocale()] ?? getLocale();

/** Languages whose own Intl data writes USD as a bare `$`; they get the ISO code instead. */
const CODE_LABELLED: readonly Locale[] = ['de', 'ru'];

const moneyOptions = (min: number, max: number, grouping = true): Intl.NumberFormatOptions => ({
  style: 'currency',
  currency: 'USD',
  currencyDisplay: CODE_LABELLED.includes(getLocale()) ? 'code' : 'symbol',
  minimumFractionDigits: min,
  maximumFractionDigits: max,
  useGrouping: grouping,
});

const money = (n: number, min: number, max: number, grouping = true): string =>
  isEnglish()
    ? (n < 0 ? '-$' : '$') +
      Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: max, useGrouping: grouping })
    : n.toLocaleString(intlLocale(), moneyOptions(min, max, grouping));

const NUMERIC_PARTS: readonly string[] = ['integer', 'group', 'decimal', 'fraction'];

const joinParts = (parts: Intl.NumberFormatPart[]): string => parts.map((part) => part.value).join('');

/** The USD amount as Intl parts, so a caller can split it without knowing whether
 *  the locale writes the currency label before or after the digits. */
const moneyParts = (n: number, min: number, max: number): Intl.NumberFormatPart[] =>
  new Intl.NumberFormat(intlLocale(), moneyOptions(min, max)).formatToParts(n);

/** The index just past the last digit, where a trailing currency label begins. */
const afterDigits = (parts: Intl.NumberFormatPart[]): number =>
  parts.map((part) => NUMERIC_PARTS.includes(part.type)).lastIndexOf(true) + 1;

/** Fixed decimals with the page's decimal mark and no grouping, e.g. `0.87`. */
export const decimals = (n: number, digits: number): string =>
  isEnglish()
    ? n.toFixed(digits)
    : n.toLocaleString(intlLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false });

/** Format a USD amount as `$1,000.00` (two decimals). */
export const usd = (n: number): string => money(n, 2, 2);

/** Format a whole-dollar label as `$1,000` (no cents). */
export const usdWhole = (n: number): string => money(n, 0, 3);

/** USD keeping sub-cent precision, e.g. `$0.0003`; whole amounts still render
 *  as `$5.00`. For per-call charges that `usd()` would round to `$0.00`. */
export const usdFine = (n: number): string => money(n, 2, 6);

/** USD for usage costs (estimates, per-request, spend). Keeps sub-cent
 *  precision while the amount is under $1 (so `$0.0065` doesn't round to a
 *  flat `$0.01`), but renders larger figures as clean `$1,234.56` rather than
 *  trailing six decimals. Use for cost displays; use `usd()` for money moved
 *  (wallet balance, top-ups, fees, charges). */
export const usdCost = (n: number): string =>
  Math.abs(n) > 0 && Math.abs(n) < 1 ? usdFine(n) : usd(n);

/** A balance change as `usdCost` writes it, with its sign: `-$3.00` taken,
 *  `+$3.00` given back. */
export const usdSigned = (n: number): string => (n > 0 ? '+' : '') + usdCost(n);

/**
 * USD at a fixed number of decimals, e.g. `usdFixed(0.0015, 4)` -> `$0.0015`.
 *
 * For a column of per-call prices that has to line up on the decimal point:
 * `usdFine` and `usdCost` drop trailing zeros, so a column rendered through
 * them runs ragged. Every caller shows a sub-dollar figure, so this deliberately
 * carries no thousands separator.
 */
export const usdFixed = (n: number, digits: number): string =>
  isEnglish() ? `$${n.toFixed(digits)}` : money(n, digits, digits, false);

/** A USD amount as a bare decimal at the precision of one charge unit
 *  ($0.00001), e.g. `0.01250`: no currency label, no grouping, always a `.`
 *  mark. For files a program reads (the usage CSV), never for display. */
export const usdCsv = (n: number): string => n.toFixed(5);

/** A USD rate with whole dollars left unadorned: `$5`, `$2.50`. For prose,
 *  where `$5.00` claims a precision the sentence is not making. */
export const usdRate = (n: number): string =>
  Number.isInteger(Math.round(n * 100) / 100) ? money(Math.round(n), 0, 0, false) : usdFixed(n, 2);

/** Three significant figures on an abbreviated value: one decimal below 100
 *  (`32.7k`, `1.2M`), whole at or above it (`840k`, `184M`). A trailing `.0` is
 *  dropped, so five million reads `5M` rather than `5.0M`. */
const abbrev = (v: number, suffix: string): string =>
  (v >= 100 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, '')) + suffix;

/**
 * Request count for any figure a reader can compare or add, e.g. `1.2M`,
 * `840,000`, `33,602`, `8,511`, `402`.
 *
 * Counts are additive across the metric-definition boundary: a platform's total
 * is exactly the sum of its endpoints' counts. The formatter has to keep that
 * visible, which decides the threshold.
 *
 * Below 1,000,000 nothing is abbreviated. An abbreviation carries three
 * significant figures at most, so it rounds an exact identity into a visible
 * contradiction: Instagram's 22 endpoints sum to exactly 10,495 and the header
 * printed `10.5k`, on the one page where a reader is most likely to check. The
 * threshold started at 10,000 for that reason and the first real platform to
 * cross it broke the property again, so it is now set past every count this
 * network can produce. Grouped digits cost one character over `10.5k`, fit
 * every surface that renders a count, and let a reader finish the addition.
 *
 * One threshold, one rendering: the `/usage` headline tile is not summed
 * against anything, but it sits above a ranked column whose rows use this same
 * helper, and a tile reading `33.6k` over rows reading `3,825` mixes two
 * formats for the same quantity on one page. That is what the page's deleted
 * `compact()` did.
 *
 * At and above 1,000,000 (a figure no surface here reaches yet) the
 * abbreviation keeps three significant figures.
 *
 * Chart gridline labels want the opposite trade and use `fmtReqTick`.
 */
export const fmtReq = (n: number): string => {
  if (n < 1_000_000) return Math.round(n).toLocaleString(intlLocale());
  // Scaled threshold, so 999,500,000 reads `1B` rather than `1000M`.
  if (n >= 999_500_000) return abbrev(n / 1_000_000_000, 'B');
  return abbrev(n / 1_000_000, 'M');
};

/** Coarse request count for chart gridline labels: `8k`, `20k`, `1.2M`.
 *
 *  Axis ticks are already round (`niceStep` emits 1/2/5 x 10^k), so rounding to
 *  the thousand loses nothing, and the label gutter is only a few characters
 *  wide. Never use this for a figure a reader might add up - `fmtReq` is the one
 *  that keeps counts commensurable. */
export const fmtReqTick = (n: number): string => {
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return (v >= 10 ? Math.round(v).toString() : v.toFixed(1)) + 'M';
  }
  if (n >= 1_000) return Math.round(n / 1_000) + 'k';
  return Math.round(n).toString();
};

/** Latency, e.g. `220ms`. */
export const fmtMs = (n: number): string => `${Math.round(n)}ms`;

/** Latency in seconds to one decimal, e.g. `3.0s`. For multi-second scraping
 *  calls where millisecond precision is not meaningful. */
export const fmtSec = (ms: number): string => `${decimals(ms / 1000, 1)}s`;

/** A percentage to one decimal, e.g. `99.9%`. The default for a rate a reader
 *  compares at a glance: pass rate, success share. */
export const pct = (n: number): string => `${decimals(n, 1)}%`;

/** A percentage to two decimals, e.g. `99.95%`, for a rate where a tenth of a
 *  point hides the difference that matters (uptime, error rate, engagement). */
export const pctFine = (n: number): string => `${decimals(n, 2)}%`;

/** A count with every digit grouped at any size, e.g. `1,200,000`; unlike
 *  `fmtReq`, never abbreviated. */
export const fmtCount = (n: number): string => n.toLocaleString(intlLocale());

/** The gateway's health-window label in words, e.g. `30d` -> `30 days`, so
 *  prose can name the window the data reports. Any other shape passes through. */
export const fmtWindow = (label: string): string => {
  const days = /^(\d+)d$/.exec(label);
  return days
    ? new Intl.NumberFormat(intlLocale(), { style: 'unit', unit: 'day', unitDisplay: 'long' }).format(Number(days[1]))
    : label;
};

/** Benchmark sample, with a singular label for one call. */
export const fmtCallSample = (n: number): string => {
  const count = fmtCount(n);
  return n === 1 ? m.phase2_fix_call_sample_one({ count }) : m.phase2_fix_call_sample_other({ count });
};

/**
 * The calendar day of a gateway RFC3339 instant, e.g. `2026-08-22`. Empty for
 * an absent or malformed value, which the gateway sends as `""` before its
 * first rollup.
 *
 * Sliced, never parsed through `Date`: the marketing routes prerender, so a
 * date rendered in the build's timezone and re-rendered in the reader's is a
 * hydration mismatch. The instant is already UTC and already in this shape.
 */
export const fmtUtcDay = (rfc3339: string | undefined): string =>
  rfc3339 && /^\d{4}-\d{2}-\d{2}T/.test(rfc3339) ? rfc3339.slice(0, 10) : '';

/**
 * A gateway instant in the reader's own timezone, e.g. `Aug 22, 3:04 PM`.
 *
 * The caller supplies the field bag, because every surface that shows a
 * timestamp shows a different set of fields. What is shared, and what this owns,
 * is the locale (`en-US` for English) and the fallback: a value `Date` cannot parse renders
 * verbatim rather than as `Invalid Date`.
 */
export const fmtInstant = (iso: string, options: Intl.DateTimeFormatOptions): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(intlLocale(), options);
};

/** `Aug 22, 2026`. */
export const DAY_STAMP: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/** `Aug 22, 3:04 PM` - no year, for a timestamp inside a recent window. */
export const MINUTE_STAMP: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

/** `Aug 22, 2026, 3:04 PM`. */
export const DAY_MINUTE_STAMP: Intl.DateTimeFormatOptions = {
  ...DAY_STAMP,
  hour: 'numeric',
  minute: '2-digit',
};

/** `Aug 22, 3:04:09 PM` - seconds, for a log where two rows can share a minute. */
export const SECOND_STAMP: Intl.DateTimeFormatOptions = { ...MINUTE_STAMP, second: '2-digit' };

/** `Aug 22, 2026, 3:04:09 PM`. */
export const DAY_SECOND_STAMP: Intl.DateTimeFormatOptions = {
  ...DAY_MINUTE_STAMP,
  second: '2-digit',
};

/**
 * A date-only `YYYY-MM-DD` as `Aug 22, 2026` (`short`) or `August 22, 2026`
 * (`long`).
 *
 * Pinned to UTC for the same reason as `fmtUtcDay`: the editorial routes
 * prerender, so a date rendered in the build's timezone and re-rendered in the
 * reader's is a hydration mismatch.
 */
export const fmtUtcDate = (iso: string, month: 'short' | 'long'): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(intlLocale(), {
    year: 'numeric',
    month,
    day: 'numeric',
    timeZone: 'UTC',
  });

/** A date-only `YYYY-MM-DD` with its weekday, `Wed, Sep 23, 2026`; UTC for the reason `fmtUtcDate` gives. */
export const fmtUtcWeekday = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(intlLocale(), {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

/** A date-only `YYYY-MM-DD` as a chart day, `Aug 28`; UTC for the reason `fmtUtcDate` gives. */
export const fmtUtcMonthDay = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * A USD amount split into the figure and a trailing currency label, for a display
 * that sets the label smaller. `$0.00` has no label after it; `0,00 USD` has.
 */
export const usdSplit = (n: number): { amount: string; suffix: string } => {
  const parts = moneyParts(n, 2, 2);
  const end = afterDigits(parts);
  return { amount: joinParts(parts.slice(0, end)), suffix: joinParts(parts.slice(end)) };
};

/**
 * The currency label a money input sits beside, on whichever side the locale
 * writes it: `$` before in English, `US$` before in pt-BR, `USD` after in German.
 * Read from the same Intl parts as every other amount, so it cannot disagree.
 */
export const usdLabel = (): { prefix: string; suffix: string } => {
  const parts = moneyParts(1, 0, 0);
  const start = parts.findIndex((part) => NUMERIC_PARTS.includes(part.type));
  return {
    prefix: joinParts(parts.slice(0, start)).trim(),
    suffix: joinParts(parts.slice(afterDigits(parts))).trim(),
  };
};

/**
 * Splits a balance into whole-dollar and 4-decimal parts for the live wallet
 * display. `dollars` carries a leading currency label, `suffix` a trailing one;
 * each locale fills whichever side it writes the label on.
 */
export const fmtBalance = (n: number) => {
  const parts = moneyParts(n, 4, 4);
  const decimalAt = parts.findIndex((part) => part.type === 'decimal');
  const end = afterDigits(parts);
  return {
    dollars: joinParts(parts.slice(0, decimalAt)),
    cents: joinParts(parts.slice(decimalAt, end)),
    suffix: joinParts(parts.slice(end)),
  };
};

/** A count in thousands to one decimal, e.g. `2.4K`; below a thousand, the bare count. */
export const fmtThousands = (n: number): string => (n >= 1000 ? `${decimals(n / 1000, 1)}K` : `${n}`);
